import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import ViewInvoiceModal from './ViewInvoiceModal';
import RefundModal from './RefundModal';

const PaymentHistoryTab = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [dateRange, setDateRange] = useState('last30days');
  const [viewInvoiceModal, setViewInvoiceModal] = useState({ isOpen: false, payment: null });
  const [refundModal, setRefundModal] = useState({ isOpen: false, payment: null });

  const [payments, setPayments] = useState([
    {
      id: 1,
      clientName: 'Acme Corp',
      plan: 'Enterprise',
      amount: 14999,
      paymentDate: '2026-02-05',
      status: 'success',
      invoiceId: 'INV-2026-001'
    },
    {
      id: 2,
      clientName: 'Tech Solutions Ltd',
      plan: 'Scale',
      amount: 5999,
      paymentDate: '2026-02-04',
      status: 'success',
      invoiceId: 'INV-2026-002'
    },
    {
      id: 3,
      clientName: 'StartupXYZ',
      plan: 'Growth',
      amount: 2999,
      paymentDate: '2026-02-03',
      status: 'failed',
      invoiceId: 'INV-2026-003'
    },
    {
      id: 4,
      clientName: 'Digital Ventures',
      plan: 'Growth',
      amount: 2999,
      paymentDate: '2026-02-02',
      status: 'pending',
      invoiceId: 'INV-2026-004'
    }
  ]);

  const handleExportCSV = () => {
    console.log('Exporting payment history to CSV...');
  };

  const handleViewInvoice = (payment) => {
    setViewInvoiceModal({ isOpen: true, payment });
  };

  const handleRefund = (payment) => {
    setRefundModal({ isOpen: true, payment });
  };

  const filteredPayments = payments?.filter(payment => {
    const matchesSearch = payment?.clientName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         payment?.invoiceId?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesStatus = statusFilter === 'all' || payment?.status === statusFilter;
    const matchesPlan = planFilter === 'all' || payment?.plan === planFilter;
    return matchesSearch && matchesStatus && matchesPlan;
  });

  const getStatusBadge = (status) => {
    const config = {
      success: { bg: 'bg-success/10', text: 'text-success', label: 'Success' },
      failed: { bg: 'bg-destructive/10', text: 'text-destructive', label: 'Failed' },
      pending: { bg: 'bg-warning/10', text: 'text-warning', label: 'Pending' }
    };
    const statusConfig = config?.[status] || config?.success;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusConfig?.bg} ${statusConfig?.text}`}>
        {statusConfig?.label}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-card border border-border rounded-lg p-4 md:p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="relative">
            <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              placeholder="Search client or invoice..."
              className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e?.target?.value)}
            className="px-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            <option value="today">Today</option>
            <option value="last7days">Last 7 Days</option>
            <option value="last30days">Last 30 Days</option>
            <option value="last90days">Last 90 Days</option>
            <option value="custom">Custom Range</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e?.target?.value)}
            className="px-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            <option value="all">All Status</option>
            <option value="success">Success</option>
            <option value="failed">Failed</option>
            <option value="pending">Pending</option>
          </select>
          <select
            value={planFilter}
            onChange={(e) => setPlanFilter(e?.target?.value)}
            className="px-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            <option value="all">All Plans</option>
            <option value="Free">Free</option>
            <option value="Growth">Growth</option>
            <option value="Scale">Scale</option>
            <option value="Enterprise">Enterprise</option>
          </select>
        </div>
        <div className="mt-4 flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCSV}
            iconName="Download"
            iconPosition="left"
          >
            Export CSV
          </Button>
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block bg-card border border-border rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Client Name</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Plan</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Amount</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Payment Date</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Invoice ID</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredPayments?.map((payment) => (
              <tr key={payment?.id} className="border-b border-border hover:bg-muted/30 transition-smooth">
                <td className="px-4 py-4 font-medium text-foreground">{payment?.clientName}</td>
                <td className="px-4 py-4 text-foreground">{payment?.plan}</td>
                <td className="px-4 py-4 text-foreground font-semibold">₹{payment?.amount?.toLocaleString()}</td>
                <td className="px-4 py-4 text-foreground">{new Date(payment?.paymentDate)?.toLocaleDateString()}</td>
                <td className="px-4 py-4">{getStatusBadge(payment?.status)}</td>
                <td className="px-4 py-4 text-foreground font-mono text-sm">{payment?.invoiceId}</td>
                <td className="px-4 py-4">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleViewInvoice(payment)}
                      className="p-2 hover:bg-muted rounded-md transition-smooth"
                      title="View Invoice"
                    >
                      <Icon name="Eye" size={16} className="text-primary" />
                    </button>
                    {payment?.status === 'success' && (
                      <button
                        onClick={() => handleRefund(payment)}
                        className="p-2 hover:bg-destructive/10 rounded-md transition-smooth"
                        title="Refund"
                      >
                        <Icon name="RotateCcw" size={16} className="text-destructive" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden space-y-4">
        {filteredPayments?.map((payment) => (
          <div key={payment?.id} className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-semibold text-foreground">{payment?.clientName}</h3>
                <p className="text-sm text-muted-foreground mt-1">{payment?.plan} Plan</p>
              </div>
              {getStatusBadge(payment?.status)}
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <p className="text-xs text-muted-foreground">Amount</p>
                <p className="text-sm font-semibold text-foreground">₹{payment?.amount?.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Date</p>
                <p className="text-sm font-semibold text-foreground">{new Date(payment?.paymentDate)?.toLocaleDateString()}</p>
              </div>
              <div className="col-span-2">
                <p className="text-xs text-muted-foreground">Invoice ID</p>
                <p className="text-sm font-mono text-foreground">{payment?.invoiceId}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleViewInvoice(payment)}
                iconName="Eye"
                iconPosition="left"
                fullWidth
              >
                View
              </Button>
              {payment?.status === 'success' && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleRefund(payment)}
                  iconName="RotateCcw"
                  iconPosition="left"
                  fullWidth
                >
                  Refund
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Modals */}
      <ViewInvoiceModal
        isOpen={viewInvoiceModal?.isOpen}
        payment={viewInvoiceModal?.payment}
        onClose={() => setViewInvoiceModal({ isOpen: false, payment: null })}
      />
      <RefundModal
        isOpen={refundModal?.isOpen}
        payment={refundModal?.payment}
        onClose={() => setRefundModal({ isOpen: false, payment: null })}
      />
    </div>
  );
};

export default PaymentHistoryTab;